import React, { useState, useEffect } from 'react';
import { db } from '../lib/firebase';
import { collection, onSnapshot, query, orderBy, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { Transaction } from '../types';
import { 
  FileText, 
  Search, 
  Calendar, 
  Trash2, 
  Edit, 
  ChevronDown, 
  ChevronUp,
  Download,
  Filter,
  X,
  Save
} from 'lucide-react';
import { formatCurrency, cn } from '../lib/utils';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';
import { motion, AnimatePresence } from 'motion/react';

const FinancialReport: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const [filterDate, setFilterDate] = useState<string>('');

  useEffect(() => {
    const q = query(collection(db, 'transactions'), orderBy('timestamp', 'desc'));
    const unsub = onSnapshot(q, (snapshot) => {
      setTransactions(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Transaction)));
    });
    return () => unsub();
  }, []);

  const handleDelete = async (id: string) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus laporan transaksi ini?')) {
      try {
        await deleteDoc(doc(db, 'transactions', id));
      } catch (error) {
        console.error("Error deleting transaction:", error);
        alert("Gagal menghapus transaksi.");
      }
    }
  };

  const handleEdit = (transaction: Transaction) => {
    setEditingTransaction({ ...transaction });
    setIsEditModalOpen(true);
  };

  const saveEdit = async () => {
    if (!editingTransaction) return;
    try {
      const { id, ...data } = editingTransaction;
      await updateDoc(doc(db, 'transactions', id), data as any);
      setIsEditModalOpen(false);
      setEditingTransaction(null);
    } catch (error) {
      console.error("Error updating transaction:", error);
      alert("Gagal memperbarui transaksi.");
    }
  };

  const filteredTransactions = transactions.filter(t => {
    const matchesSearch = t.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         t.cashierName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         t.id.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesDate = filterDate ? t.timestamp.startsWith(filterDate) : true;
    
    return matchesSearch && matchesDate;
  });

  const totalRevenue = filteredTransactions.reduce((sum, t) => sum + t.total, 0);

  return (
    <div className="max-w-7xl mx-auto space-y-8 pb-20">
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-3xl font-serif font-bold text-primary flex items-center gap-3">
            <FileText size={32} />
            Laporan Keuangan
          </h1>
          <p className="text-stone-500 text-sm">Riwayat transaksi kasir dan ringkasan pendapatan.</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="pro-card px-6 py-3 bg-primary text-white shadow-lg shadow-pink-200/50">
            <p className="text-[10px] font-bold uppercase tracking-widest opacity-80">Total Pendapatan</p>
            <p className="text-xl font-mono font-bold">{formatCurrency(totalRevenue)}</p>
          </div>
        </div>
      </header>

      <section className="pro-card p-6 md:p-8 space-y-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
            <input
              type="text"
              placeholder="Cari pelanggan, kasir, atau ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pro-input pl-12 py-3"
            />
          </div>
          <div className="relative w-full md:w-64">
            <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
            <input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className="pro-input pl-12 py-3"
            />
          </div>
          {filterDate && (
            <button 
              onClick={() => setFilterDate('')}
              className="p-3 text-stone-400 hover:text-primary transition-colors"
            >
              <X size={20} />
            </button>
          )}
        </div>

        <div className="overflow-x-auto rounded-[32px] border border-stone-100 custom-scrollbar">
          <table className="w-full text-left border-collapse min-w-[600px]">
            <thead>
              <tr className="bg-stone-50/50">
                <th className="px-4 py-3 md:px-6 md:py-4 text-[10px] font-bold text-stone-400 uppercase tracking-widest">Tanggal & Waktu</th>
                <th className="px-4 py-3 md:px-6 md:py-4 text-[10px] font-bold text-stone-400 uppercase tracking-widest">Pelanggan</th>
                <th className="px-4 py-3 md:px-6 md:py-4 text-[10px] font-bold text-stone-400 uppercase tracking-widest">Kasir</th>
                <th className="px-4 py-3 md:px-6 md:py-4 text-[10px] font-bold text-stone-400 uppercase tracking-widest text-right">Total</th>
                <th className="px-4 py-3 md:px-6 md:py-4 text-[10px] font-bold text-stone-400 uppercase tracking-widest text-center">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-50">
              {filteredTransactions.map((t) => (
                <React.Fragment key={t.id}>
                  <tr className={cn(
                    "hover:bg-stone-50/30 transition-colors cursor-pointer",
                    expandedId === t.id && "bg-stone-50/50"
                  )} onClick={() => setExpandedId(expandedId === t.id ? null : t.id)}>
                    <td className="px-6 py-4">
                      <p className="text-sm font-bold text-stone-800">
                        {format(new Date(t.timestamp), 'dd MMM yyyy', { locale: id })}
                      </p>
                      <p className="text-[10px] text-stone-400 font-mono">
                        {format(new Date(t.timestamp), 'HH:mm:ss')}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium text-stone-700">{t.customerName}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-stone-600">{t.cashierName}</p>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <p className="text-sm font-mono font-bold text-primary">{formatCurrency(t.total)}</p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2" onClick={e => e.stopPropagation()}>
                        <button 
                          onClick={() => handleEdit(t)}
                          className="p-2 text-stone-400 hover:text-blue-500 hover:bg-blue-50 rounded-xl transition-all"
                          title="Edit"
                        >
                          <Edit size={16} />
                        </button>
                        <button 
                          onClick={() => handleDelete(t.id)}
                          className="p-2 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                          title="Hapus"
                        >
                          <Trash2 size={16} />
                        </button>
                        <div className="text-stone-300 ml-1">
                          {expandedId === t.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </div>
                      </div>
                    </td>
                  </tr>
                  <AnimatePresence>
                    {expandedId === t.id && (
                      <tr>
                        <td colSpan={5} className="px-6 py-0">
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="py-6 px-4 space-y-4 bg-stone-50/30 rounded-2xl mb-4 border border-stone-100">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-3">
                                  <p className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">Detail Produk</p>
                                  <div className="space-y-2">
                                    {t.items.map((item, idx) => (
                                      <div key={idx} className="flex justify-between text-sm">
                                        <span className="text-stone-600">{item.name} <span className="text-stone-400">x{item.quantity}</span></span>
                                        <span className="font-mono text-stone-800">{formatCurrency(item.price * item.quantity)}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                <div className="space-y-3">
                                  <p className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">Detail Pembayaran</p>
                                  <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                      <span className="text-stone-500">Total Tagihan</span>
                                      <span className="font-mono font-bold">{formatCurrency(t.total)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-stone-500">Dibayar</span>
                                      <span className="font-mono">{formatCurrency(t.paymentAmount)}</span>
                                    </div>
                                    <div className="flex justify-between pt-2 border-t border-stone-100">
                                      <span className="text-stone-500 font-bold">Kembalian</span>
                                      <span className="font-mono font-bold text-green-600">{formatCurrency(t.change)}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        </td>
                      </tr>
                    )}
                  </AnimatePresence>
                </React.Fragment>
              ))}
              {filteredTransactions.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-20 text-center">
                    <div className="w-20 h-20 bg-stone-50 rounded-full flex items-center justify-center mx-auto text-stone-200 mb-4">
                      <FileText size={40} />
                    </div>
                    <p className="text-stone-400 font-serif italic">Belum ada data transaksi.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Edit Modal */}
      <AnimatePresence>
        {isEditModalOpen && editingTransaction && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsEditModalOpen(false)}
              className="absolute inset-0 bg-stone-400/20 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="relative bg-white w-full max-w-md pro-card overflow-hidden"
            >
              <div className="p-6 border-b border-stone-50 flex justify-between items-center bg-stone-50/50">
                <h2 className="text-xl font-serif font-bold text-stone-800">Edit Laporan Transaksi</h2>
                <button onClick={() => setIsEditModalOpen(false)} className="p-2 text-stone-400 hover:bg-white rounded-xl transition-colors">
                  <X size={24} />
                </button>
              </div>

              <div className="p-8 space-y-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-stone-400 uppercase tracking-widest ml-1">Nama Pelanggan</label>
                  <input
                    type="text"
                    value={editingTransaction.customerName}
                    onChange={(e) => setEditingTransaction({ ...editingTransaction, customerName: e.target.value })}
                    className="pro-input font-bold"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-stone-400 uppercase tracking-widest ml-1">Total Transaksi (Rp)</label>
                  <input
                    type="number"
                    value={editingTransaction.total}
                    onChange={(e) => setEditingTransaction({ ...editingTransaction, total: parseInt(e.target.value) || 0 })}
                    className="pro-input font-mono"
                  />
                </div>

                <div className="p-4 bg-amber-50 rounded-2xl border border-amber-100">
                  <p className="text-[10px] text-amber-700 font-bold uppercase tracking-widest mb-1">Peringatan</p>
                  <p className="text-xs text-amber-600 leading-relaxed">
                    Mengedit laporan keuangan hanya mengubah data administratif. Pastikan data tetap akurat sesuai dengan fisik uang di kasir.
                  </p>
                </div>

                <button
                  onClick={saveEdit}
                  className="pro-button w-full flex items-center justify-center gap-2 py-4"
                >
                  <Save size={20} />
                  Simpan Perubahan
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FinancialReport;
